import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { SearchLogComponent, SystemLogFilter } from 'components/admin/system-log/search-log.component';
import { CompanyInformationComponent } from 'components/company/company-information.component';
import { ListContactComponent } from 'components/company/contact/list-contact.component';
import { ViewCompanyComponent } from 'components/company/viewcompany.component';
import { ButtonComponent } from 'components/input/button/button.component';
import { EditableContent, EditableContentComponent, SingleFocusService } from 'components/input/editable-content/editable-content.component';
import { FileUploadComponent, UploadedFile } from 'components/input/file-upload/file-upload';
import { QuestionnaireComponent } from 'components/questionnaire/questionnaire.component';
import { EditQuestionnaireRepliesComponent } from 'components/questionnaire/edit-questionnaire-replies.component';
import { EditOrderInstallmentsComponent } from 'components/sale/edit-order-installments/edit-order-installments.component';
import { CreateOrderCancellationComponent } from 'components/sale/order-cancellation/create-order-cancellation.component';
import { CreateOrderWarningComponent } from 'components/sale/order-warning/create-order-warning.component';
import { FileDownloadComponent } from 'components/shared/files/file-download.component';
import { TaskActionLogComponent } from 'components/task/taskflow/task-action-log.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { CredentialType } from 'enums/CredentialType';
import { ProductPlanSettingType } from 'enums/ProductPlanSettingType';
import 'extensions/deep-copy';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { OrderCancellationModel } from 'models/generated/Entities.GormCrm.OrderCancellation';
import { ProductPlanSettingModel } from 'models/generated/Entities.GormCrm.ProductPlanSetting';
import { QuestionnaireModel } from 'models/generated/Entities.GormCrm.Questionnaire';
import { LogRequestModel } from 'models/generated/Logging.Api.Models.LogRequest';
import { KeepExistingProductPlanOnDiscontinuationSettingDtoModel } from 'models/generated/Models.KeepExistingProductPlanOnDiscontinuationSettingDto';
import { SelectListItemModel } from 'models/SelectList';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { CompanyService } from 'services/company.service';
import { CredentialService } from 'services/credential.service';
import { DataTracking } from 'services/data-tracking';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ProductService } from 'services/product.service';
import { ProductPlanService } from 'services/productplan.service';
import { QuestionnaireService } from 'services/questionnaire.service';
import { SalesService } from 'services/sales.service';
import { UserService } from 'services/user.service';
import { OrderLinesComponent } from './order-lines.component';
import { ApproveOrderComponent } from './approve-order.component';
import { QuestionTagConstants } from 'constants/QuestionTagConstants';
import { PushService } from 'services/push.service';
import { Subscription } from 'rxjs';
import { EditOrderExternalsComponent } from 'components/sale/edit-order-externals/edit-order-externals.component';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { ReasonService } from 'services/reason.service';
import { OrderCommissionDetailsComponent } from './order-commission-details.component';
import { OrderPlanDiscontinuationsComponent } from './order-plan-discontinuations.component';

export type PanelType = 'company' | 'questionnaire' | 'communication';

@Component({
	selector: 'order',
	templateUrl: './order.component.html',
	providers: [DataTracking, SingleFocusService],
	imports: [Layout, Pipes, ListView, Inputs, EditableContent, FileDownloadComponent, FileUploadComponent, OrderLinesComponent, OrderPlanDiscontinuationsComponent, CompanyInformationComponent, ListContactComponent, QuestionnaireComponent, TaskActionLogComponent, OrderCommissionDetailsComponent]
})
@ComponentUrl('order')
export class OrderComponent implements OnDestroy {

	@ViewChild('buttonReject') submitButtonReject!: ButtonComponent;
	@ViewChild('buttonWarning') submitButtonOrderWarning!: ButtonComponent;
	@ViewChild('buttonConfirm') submitButtonConfirm!: ButtonComponent;
	@ViewChild('orderDetailsEditor') orderDetailsEditor!: EditableContentComponent;

	public order?: OrderModel;
	public editMode = false;
	public Credentials = CredentialType;
	public uploadedFiles: UploadedFile[] = [];
	public questionnaires: QuestionnaireModel[];
	public saleTeamList: SelectListItemModel[] = [];
	public userList: SelectListItemModel[];
	public maxSignDate: Date = new Date();
	public violations?: { message: string, parameters: Object }[];
	public showPanel: PanelType = 'questionnaire';
	public showUpload = false;
	public hasApproveOrderSpecialConditions = false;
	public totalDiscount: number = 0;
	public discountReason?: ReasonModel;

	@Output() public orderCompleted = new EventEmitter();

	private specialConditionsQuestionIds: string[] = [];
	private replyChangeSubscription: Subscription;
	private productPlanSettings?: ProductPlanSettingModel[];
	private _orderId?: string;

	private trackedOrder?: OrderModel; // "actual" order model stored while editing, to avoid conflicts from push messages, etc.
	public defaultNumberOfInstallments = 1;

	@UrlParam @Input()
	public set orderId(id: string | undefined) {
		if (!id || id === '') return;
		this._orderId = id;
		this.navigation.refreshUrl();

		this.order = undefined;
		this.violations = undefined;
		this.loadOrder().then(() => {
			if (!this.order?.companyId) return;

			this.navigation.openNavigationView(this, 'company-related-views', ViewCompanyComponent).then(viewCompany => { viewCompany.companyId = this.order!.companyId! });
			this.refreshSpecialConditionsStatus();
		});
	}
	public get orderId() { return this.order?.id ?? this._orderId; }
	public get cancellation(): OrderCancellationModel | undefined { return this.order?.orderCancellations[0]; }

	constructor(
		public readonly salesService: SalesService,
		public readonly companyService: CompanyService,
		private readonly navigation: NavigationService,
		private readonly questionnaireService: QuestionnaireService,
		private readonly productPlanService: ProductPlanService,
		private readonly productService: ProductService,
		private readonly tracker: DataTracking,
		private readonly notificationService: NotificationService,
		private readonly credentialService: CredentialService,
		private readonly userService: UserService,
		private readonly reasonService: ReasonService,
		pushService: PushService) {
		this.questionnaires = [];
		this.productPlanService.getProductPlanSettings().then(settings => { this.productPlanSettings = settings; this.validateOrder(); });
		this.questionnaireService.getQuestionnaireByTag('sale').then(questionnaire => questionnaire && this.questionnaires?.push(questionnaire));

		this.userList = userService.getUserSelectList(true);
		for (const team of userService.activeTeams.filter(team => team.sale)) {
			this.saleTeamList.push(new SelectListItemModel(team.id!, team.name));
		}

		this.replyChangeSubscription = pushService.onQuestionReplyChange.subscribe(async (event) => {
			const questionId = event.data.questionId;
			if (this.specialConditionsQuestionIds.some(id => id == questionId)) this.refreshSpecialConditionsStatus();
		});
	}

	ngOnDestroy() {
		this.replyChangeSubscription.unsubscribe();
	}

	private async refreshSpecialConditionsStatus() {
		if (!this.order?.companyId) return;
		const replies = await this.questionnaireService.getQuestionRepliesByTag(QuestionTagConstants.approveOrderSpecialConditions, this.order.companyId, this.order.id, undefined);
		this.hasApproveOrderSpecialConditions = replies.some(reply => reply.replyData === "[true]");
		this.specialConditionsQuestionIds = replies.map(r => r.questionId!); // Probably only one matching question, but technically the tag can be used on more
	}

	public warningOrderMightBeDowngrade = false;

	private async loadOrder() {
		if (!this.orderId) return;
		this.order = await this.salesService.getOrderById(this.orderId, this.tracker);
		if (this.order.orderWarnings.length > 0) this.showPanel = 'communication';
		this.validateOrder();
		this.maxSignDate = this.order.created!;
		this.totalDiscount = this.salesService.getOrderLinesDiscountSum(this.order.orderLines) * -1;

		if (this.order.discountReasonId) this.reasonService.getReason(this.order.discountReasonId).then(reason => this.discountReason = reason);
		else this.discountReason = undefined;

		const discontinuedPlansAnnualValueForRenewal = this.order.orderProductPlanDiscontinuations.reduce((sum, discontinuedPlan) => sum + (discontinuedPlan.productPlan?.annualValueForRenewal ?? 0), 0) + (this.order.externalAnnualValueForRenewal ?? 0);
		const newPlansAnnualValueForRenewal = this.order.orderLines.reduce((sum, orderLine) => sum + (this.productService.getVariantPeriod(orderLine.productVariant!) != null ? (orderLine.totalPrice / ((this.productService.getVariantPeriod(orderLine.productVariant!) ?? 0) / 12)) : 0), 0);

		this.warningOrderMightBeDowngrade = discontinuedPlansAnnualValueForRenewal > newPlansAnnualValueForRenewal;

	}

	get orderWarnings() { return this.order?.orderWarnings?.filter(w => !w.closed) ?? []; }

	getTotalPrice(): number {
		return this.order ? this.salesService.getOrderLinesTotalPriceSum(this.order.orderLines) : 0;
	}

	getTotalAdjustmentAmount(): number {
		return this.order ? this.salesService.getTotalAdjustmentAmount(this.order) : 0;
	}

	calculateSupersededRemainingAmountForOrder(): number {
		return this.order ? this.salesService.calculateSupersededRemainingAmountForOrder(this.order) : 0;
	}

	toggleWithholdCommission() {
		this.notificationService.confirm(this.order!.withholdCommission ? 'order.confirm-remove-withhold-commission' : 'order.confirm-set-withhold-commission').subscribe(result => {
			if (result) {
				this.order!.withholdCommission = !this.order!.withholdCommission;
				this.salesService.setWithholdCommission(this.order!.id!, this.order!.withholdCommission);
			}
		});
	}

	toggleAutoDelivery() {
		this.notificationService.confirm(this.order!.autoDelivery ? 'order.confirm-remove-auto-delivery' : 'order.confirm-set-auto-delivery').subscribe(result => {
			if (result) {
				this.order!.autoDelivery = !this.order!.autoDelivery;
				this.salesService.setAutoDelivery(this.order!.id!, this.order!.autoDelivery);
			}
		});
	}

	validateOrder() {
		this.violations = [];
		this.validateKeepProductPlanSetting();
		this.validateDisallowedDiscontinuations();
	}
	validateKeepProductPlanSetting() {
		if (!this.order || !this.productPlanSettings) return;

		const discontinuations = this.order.orderProductPlanDiscontinuations;
		const productVariants = this.order.orderLines.flatMap(line => line.productVariant);
		if (discontinuations && productVariants) {
			const keepProductPlanSettings = this.productPlanSettings.filter(s => s.type == ProductPlanSettingType.KeepExistingProductPlanOnDiscontinuation);
			for (const planSetting of keepProductPlanSettings) {
				const setting = JSON.parse(planSetting.data) as KeepExistingProductPlanOnDiscontinuationSettingDtoModel;
				const plansWithSourceTag = discontinuations.filter(d => d.productPlan?.productPlanTags.some(t => t.tag == setting.sourceTag));
				const productsWithDestinationTag = productVariants.filter(p => p?.product?.productTags.some(t => t.tag == ProductPlanTagConstants.prefixForProductTags + setting.destinationTag));

				if (plansWithSourceTag.length > 1 && productsWithDestinationTag.length > 0)
					this.violations?.push({ message: "order.violation-of-keep-productplan-setting.multiple-discontinuations", parameters: { sourceTag: setting.sourceTag, destinationTag: setting.destinationTag } })
				if (plansWithSourceTag.length > 0 && productsWithDestinationTag.length > 1)
					this.violations?.push({ message: "order.violation-of-keep-productplan-setting.multiple-new-continuations", parameters: { sourceTag: setting.sourceTag, destinationTag: setting.destinationTag } })
			};
		}
	}
	validateDisallowedDiscontinuations() {
		if (!this.order || !this.productPlanSettings) return;

		const discontinuations = this.order.orderProductPlanDiscontinuations;
		const productVariants = this.order.orderLines.flatMap(line => line.productVariant);
		if (discontinuations && productVariants) {
			const cannotDiscontinueProductPlanRules = this.productPlanSettings.filter(s => s.type == ProductPlanSettingType.CannotBeDiscontinued);
			for (const planSetting of cannotDiscontinueProductPlanRules) {
				const tag = planSetting.data;
				if (discontinuations.some(d => d.productPlan?.productPlanTags.some(t => t.tag == tag)))
					this.violations?.push({ message: "order.violation-of-disallowed-product-plan-discontinuation", parameters: { tag } });
			};
		}
	}

	async approveOrder() {

		if (!this.order?.id) return;
		const orderId = this.order.id;

		const questionnaireId = await this.questionnaireService.getQuestionnaireByTag('approve-order').then(questionnaire => questionnaire?.id);
		const view = await this.navigation.openPopup(ApproveOrderComponent);
		view.orderId = orderId;
		view.questionnaireId = questionnaireId;
		view.orderLines = this.order.orderLines;
		view.approvedOrder.subscribe(async _ => {
			this.submitButtonConfirm.working = true;
			await this.salesService.approveOrder(orderId);
			this.orderCompleted.emit();
			this.navigation.closeView(view);
			this.submitButtonConfirm.succeeded();
		});

		this.submitButtonConfirm.reset();
	}

	async removeOrderWarning() {
		if (!this.hasOpenWarning()) return;
		await this.salesService.closeOrderWarning(this.order?.orderWarnings.filter(w => !w.closed)[0].id!);
		this.submitButtonOrderWarning.reset();
	}

	deleteContract(attachmentId: string) {
		this.notificationService.confirm('order.attachment-delete.confirm-message').subscribe(result => {
			if (result)
				this.salesService.deleteSalesAttachment(attachmentId).then(result => {
					this.uploadedFiles = [];
				});
		});
	}

	async createOrderWarning() {
		if (!this.order?.id) return;
		const orderId = this.order.id;
		const view = await this.navigation.openPopup(CreateOrderWarningComponent);

		view.orderId = orderId;

		this.submitButtonOrderWarning.reset();
	}

	async createOrderCancellation() {
		if (!this.order) return;
		const view = await this.navigation.openPopup(CreateOrderCancellationComponent);

		view.orderId = this.order.id!;
		view.cancellationCreated.subscribe(() => {
			this.orderCompleted.emit();
		})

		this.submitButtonReject.reset();
	}

	async showSystemLog() {
		if (!this.order) return;
		const view = await this.navigation.openPopup(SearchLogComponent);
		view.filteredEntities = [new SystemLogFilter(OrderModel, { entityIds: [this.order.id!] } as LogRequestModel)];
	}

	async editOrderSpecialConditions() {
		const questionnaireId = await this.questionnaireService.getQuestionnaireByTag('approve-order').then(questionnaire => questionnaire?.id);
		if (!this.order || !questionnaireId) return;

		const view = await this.navigation.openPopup(EditQuestionnaireRepliesComponent);
		view.header = 'order.edit-order-special-conditions-title';
		view.displayAsForm = true;
		view.orderLines = this.order.orderLines
		view.questionnaireId = questionnaireId;
	}

	async editOrderInstallments() {
		if (!this.order) return;
		const view = await this.navigation.openPopup(EditOrderInstallmentsComponent);
		view.order = this.order;
	}

	async editOrderExternals() {
		if (!this.order) return;
		const view = await this.navigation.openPopup(EditOrderExternalsComponent);
		view.order = this.order;
		
		view.updatedExternals.subscribe(async _ => {
			await this.loadOrder(); // Reload order to get updated values
			await this.checkForStartupAmountAndUpdate();
		});
	}

	async clearOrderExternals() {
		if (!this.order) return;
		this.notificationService.confirm('order.confirm-clear-externals').subscribe(async result => {
			if (result) {
				await this.salesService.updateDiscount(this.order!.id!);
				await this.salesService.updateExternals(this.order!.id!);
				await this.loadOrder(); // Reload order to get updated values
				await this.checkForStartupAmountAndUpdate();
			}
		});
	}

	async checkForStartupAmountAndUpdate() {
		// If any orderline has a startup amount (can be 0, but not undefined), recalculate the value that includes the first invoice to fit the new discount
		if (this.order?.orderLines.find(ol => ol.startupAmountExclFirstInvoice !== undefined)) {
			this.recalculateStartupAmounts();
			this.order = Object.deepCopy(this.order);
			await this.saveOrderLineChanges();
		}
	}

	canEditExternalAdjustments(): boolean {
		if (!this.order || this.editMode) return false;
		return !this.order.approvedDate && this.order.orderProductPlanDiscontinuations.length == 0 && this.order.orderCancellations.length == 0;
	}

	hasExternalAdjustments(): boolean {
		return this.order ? this.salesService.hasExternalAdjustments(this.order) : false;
	}

	validExternalAdjustments(): boolean {
		if (!this.hasExternalAdjustments()) return true;
		return (this.order?.externalAnnualValueForRenewal !== undefined && this.order?.externalRemainingAmount !== undefined && this.order?.discountReasonId !== undefined && this.order?.orderLines?.some(ol => ol.discount !== undefined));
	}

	validateDiscountValues(): boolean {
		return this.order?.orderLines.every(ol => (ol.totalPrice - (ol.discount ?? 0)) >= 0 && (ol.totalPrice - (ol.discount ?? 0)) >= (ol.startupAmount ?? 0)) ?? true;
	}

	editOrderDetails(order: OrderModel) {
		this.salesService.updateOrderDetails(order);
	}

	canEditSaleAttachments(): boolean {
		if (!this.order) return false;
		return ((this.credentialService.hasCredentials(CredentialType.DeleteContracts) || this.order.sellerId == this.userService.getCurrentUser().id) && !this.order.approvedDate);
	}

	editOrderLines() {
		if (!this.order) return;

		// Determine default installment values, as user cannot edit this while editing OrderLines
		this.defaultNumberOfInstallments =
			this.order.orderLines.find(ol => ol.startupAmount != undefined && ol.startupInstallments != undefined)?.startupInstallments
			?? this.order.orderLines.find(ol => ol.numberOfInstallments != undefined)?.numberOfInstallments
			?? 1;

		// Start buffering product list immediately so editor component doesn't have to wait for it every time. It can be a little slow
		this.productService.getAllActiveProducts(true);

		// Store the order in a separate variable to avoid conflicts from push messages, etc.
		this.trackedOrder = this.order;
		this.order = Object.deepCopy(this.order);

		// Finally, enable edit mode
		this.editMode = true;
	}
	cancelEditMode() {
		// Reset order to the stored value in order to regain the DataTracker instance
		this.order = this.trackedOrder;
		this.editMode = false;
	}

	async saveOrderLineChanges() {
		if (!this.order) return;

		await this.salesService.updateOrder(this.order.id!, this.order.orderLines, this.order.orderProductPlanDiscontinuations);
		await this.loadOrder(); // Reload order before resetting edit mode, to prevent double loading

		this.editMode = false;
	}

	canApprove(): boolean {
		if (!this.validExternalAdjustments()) return false;
		if (!this.validateDiscountValues()) return false;
		if (!this.order || this.order.approvedDate) return false;
		if (!this.violations || this.violations.length > 0) return false;
		return !this.hasOpenWarning() && this.order.saleAttachments.length > 0 && this.order.signDate != undefined;
	}

	hasDeliveredPlans(): boolean {
		if (!this.order?.created) return false;

		return this.order.orderLines.some(orderLine => orderLine.productPlans.some(plan => plan.deliveryDate && plan.deliveryDate >= this.order!.created!)) ?? false;
	}

	hasOpenWarning(): boolean {
		return (this.order?.orderWarnings.filter(w => !w.closed).length ?? 0) > 0;
	}

	recalculateStartupAmounts() {
		if (this.order) this.salesService.calculateStartupInclFirstInvoice(this.order);
	}
	
}
